import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Mail, Lock, Globe, ArrowRight, Briefcase, Users } from 'lucide-react';

const Login = () => {
  const [emailStaff, setEmailStaff] = useState('');
  const [passwordStaff, setPasswordStaff] = useState('');
  const [emailClient, setEmailClient] = useState('');
  const [passwordClient, setPasswordClient] = useState('');
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const { login } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoadingStaff(true);
    try {
      await login(emailStaff, passwordStaff);
      toast.success('Connexion reussie');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error?.message || 'Identifiants invalides');
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleClientLogin = async (e) => {
    e.preventDefault();
    setLoadingClient(true);
    try {
      await login(emailClient, passwordClient);
      toast.success('Connexion reussie');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error?.message || 'Identifiants invalides');
    } finally {
      setLoadingClient(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col" data-testid="login-page">
      {/* Language toggle */}
      <div className="absolute top-6 right-6 z-10">
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
          data-testid="language-toggle-login"
        >
          <Globe className="h-4 w-4" />{i18n.language.toUpperCase()}
        </button>
      </div>

      {/* Logo centre */}
      <div className="flex justify-center pt-10 pb-6 md:pt-14 md:pb-10">
        <img
          src="/logo.png"
          alt="LCS Immigration"
          className="h-20 md:h-24 w-auto drop-shadow-md"
          data-testid="login-logo"
        />
      </div>

      {/* Deux cartes de connexion */}
      <div className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-4xl">
          {/* Carte Employe / Admin */}
          <div className="flex flex-col" data-testid="staff-login-card">
            {/* En-tete gradient violet/bleu */}
            <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-blue-500 rounded-t-2xl px-8 py-10 text-center text-white shadow-lg">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-5">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-1">Connexion Employe</h2>
              <p className="text-white/80 text-sm">Administrateur & Employe</p>
            </div>

            {/* Formulaire */}
            <div className="bg-white rounded-b-2xl px-8 py-8 shadow-lg border border-t-0 border-slate-100 flex-1">
              <form onSubmit={handleStaffLogin} className="space-y-5" data-testid="staff-login-form">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Courriel</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={emailStaff}
                      onChange={(e) => setEmailStaff(e.target.value)}
                      placeholder="employe@lcsimmigration.com"
                      className="pl-11 h-12 border-slate-200 rounded-xl focus:border-purple-500 focus:ring-purple-500"
                      required
                      data-testid="email-staff-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="password"
                      value={passwordStaff}
                      onChange={(e) => setPasswordStaff(e.target.value)}
                      placeholder="Votre mot de passe"
                      className="pl-11 h-12 border-slate-200 rounded-xl focus:border-purple-500 focus:ring-purple-500"
                      required
                      data-testid="password-staff-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loadingStaff}
                  className="w-full h-12 bg-gradient-to-r from-violet-600 to-blue-500 hover:from-violet-700 hover:to-blue-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg disabled:opacity-60"
                  data-testid="staff-login-submit-btn"
                >
                  {loadingStaff ? 'Connexion...' : 'Se connecter'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              <div className="mt-5 text-center">
                <Link to="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700 font-semibold" data-testid="forgot-password-link-staff">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>
          </div>

          {/* Carte Client */}
          <div className="flex flex-col" data-testid="client-login-card">
            {/* En-tete gradient rouge/orange */}
            <div className="bg-gradient-to-br from-red-500 via-red-500 to-orange-400 rounded-t-2xl px-8 py-10 text-center text-white shadow-lg">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-5">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-1">Connexion Client</h2>
              <p className="text-white/80 text-sm">Espace Client</p>
            </div>

            {/* Formulaire */}
            <div className="bg-white rounded-b-2xl px-8 py-8 shadow-lg border border-t-0 border-slate-100 flex-1">
              <form onSubmit={handleClientLogin} className="space-y-5" data-testid="client-login-form">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Courriel</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={emailClient}
                      onChange={(e) => setEmailClient(e.target.value)}
                      placeholder="client@email.com"
                      className="pl-11 h-12 border-slate-200 rounded-xl focus:border-red-500 focus:ring-red-500"
                      required
                      data-testid="email-client-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="password"
                      value={passwordClient}
                      onChange={(e) => setPasswordClient(e.target.value)}
                      placeholder="Votre mot de passe"
                      className="pl-11 h-12 border-slate-200 rounded-xl focus:border-red-500 focus:ring-red-500"
                      required
                      data-testid="password-client-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loadingClient}
                  className="w-full h-12 bg-gradient-to-r from-red-500 to-orange-400 hover:from-red-600 hover:to-orange-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg disabled:opacity-60"
                  data-testid="client-login-submit-btn"
                >
                  {loadingClient ? 'Connexion...' : 'Se connecter'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>

              <div className="mt-5 flex items-center justify-between">
                <Link to="/forgot-password" className="text-sm text-red-500 hover:text-red-600 font-semibold" data-testid="forgot-password-link-client">
                  Mot de passe oublié ?
                </Link>
                <span className="text-sm text-slate-500">
                  Pas de compte?{' '}
                  <Link to="/register" className="text-red-500 hover:text-red-600 font-semibold" data-testid="register-link">
                    {`S'inscrire`}
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-xs text-slate-400">
          LCS Immigration - Lahroum Canada Services | N. permis: R713199 | +1 (514) 347-6462
        </p>
      </div>
    </div>
  );
};

export default Login;
